import { useEffect, useMemo, useRef, useState } from 'react';

const TICK_MS = 250;

type RecordingClockOptions = {
  isTicking: boolean;
  maxDurationSeconds?: number;
  /** Called on every tick while ticking at or over the limit. */
  onLimitReached: () => void;
};

/** Measures the recorded time, leaving paused time out, and shows it in whole seconds while ticking. */
export const useRecordingClock = ({ isTicking, maxDurationSeconds, onLimitReached }: RecordingClockOptions) => {
  const [durationSeconds, setDurationSeconds] = useState(0);
  const recordedMsRef = useRef(0);
  const runningSinceRef = useRef<null | number>(null);
  const onLimitReachedRef = useRef(onLimitReached);
  onLimitReachedRef.current = onLimitReached;

  const clock = useMemo(() => {
    const getElapsedMs = () =>
      recordedMsRef.current + (runningSinceRef.current === null ? 0 : Date.now() - runningSinceRef.current);

    const start = () => {
      recordedMsRef.current = 0;
      runningSinceRef.current = Date.now();
      setDurationSeconds(0);
    };

    const pause = () => {
      recordedMsRef.current = getElapsedMs();
      runningSinceRef.current = null;
    };

    const resume = () => {
      runningSinceRef.current = Date.now();
    };

    /** Stops measuring and returns the recorded time in milliseconds. */
    const stop = () => {
      pause();
      return recordedMsRef.current;
    };

    /** Forgets the recorded time; the last shown duration stays until clearDisplayedDuration() or start(). */
    const reset = () => {
      recordedMsRef.current = 0;
      runningSinceRef.current = null;
    };

    const clearDisplayedDuration = () => setDurationSeconds(0);

    return { clearDisplayedDuration, pause, resume, start, stop, getElapsedMs, reset };
  }, []);

  useEffect(() => {
    if (!isTicking) return;
    const timer = window.setInterval(() => {
      const elapsedMs = clock.getElapsedMs();
      setDurationSeconds(Math.floor(elapsedMs / 1000));

      if (maxDurationSeconds !== undefined && elapsedMs >= maxDurationSeconds * 1000) onLimitReachedRef.current();
    }, TICK_MS);

    return () => window.clearInterval(timer);
  }, [clock, isTicking, maxDurationSeconds]);

  return { clock, durationSeconds };
};
